import { useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";

const CATEGORIES = [
  { id: "trading_cards", emoji: "🃏", label: "Trading Cards", desc: "Pokémon, sports, TCGs" },
  { id: "sneakers", emoji: "👟", label: "Sneakers", desc: "Jordans, Yeezys, Nikes" },
  { id: "clothing", emoji: "👕", label: "Streetwear", desc: "Supreme, Off-White, vintage" },
  { id: "video_games", emoji: "🎮", label: "Video Games", desc: "Games, accessories, merch" },
  { id: "pc_parts", emoji: "🖥️", label: "PC Parts", desc: "GPUs, CPUs, RAM, builds" },
  { id: "phones", emoji: "📱", label: "Phones", desc: "iPhones, Androids, accessories" },
  { id: "consoles", emoji: "🕹️", label: "Consoles", desc: "PS5, Xbox, Nintendo" },
  { id: "collectibles", emoji: "🏆", label: "Collectibles", desc: "Figures, statues, limited runs" },
  { id: "watches", emoji: "⌚", label: "Watches", desc: "Rolex, AP, vintage timepieces" },
  { id: "jewelry", emoji: "💎", label: "Jewelry", desc: "Chains, rings, diamonds" },
  { id: "sports_cards", emoji: "🏅", label: "Sports Memorabilia", desc: "Signed gear, cards, jerseys" },
  { id: "luxury", emoji: "👜", label: "Luxury Goods", desc: "Handbags, accessories, designer" },
  { id: "toys_figures", emoji: "🧸", label: "Toys & Figures", desc: "LEGO, Funko Pops, vintage" },
  { id: "books_comics", emoji: "📚", label: "Books & Comics", desc: "First editions, graded comics" },
];

interface CategoryPickerModalProps {
  onComplete: (selected: string[]) => void;
  onSkip: () => void;
}

export default function CategoryPickerModal({ onComplete, onSkip }: CategoryPickerModalProps) {
  const { isDark } = useTheme();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleContinue = () => {
    const arr = Array.from(selected);
    if (arr.length > 0) {
      try { localStorage.setItem("trackly_categories", JSON.stringify(arr)); } catch (e) { /* ignore */ }
    }
    onComplete(arr);
  };

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      style={{ animation: "cpFadeIn 0.22s ease" }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/75 backdrop-blur-md" />

      {/* Card */}
      <div
        className={`relative z-10 w-full max-w-[600px] rounded-3xl overflow-hidden border flex flex-col transition-colors duration-300 ${
          isDark ? "bg-[#0a0a0a] border-white/10" : "bg-white border-black/8"
        }`}
        style={{ animation: "cpSlideUp 0.3s cubic-bezier(0.34,1.56,0.64,1)", maxHeight: "92vh" }}
      >
        {/* Top strip */}
        <div
          className={`h-1 w-full flex-shrink-0 ${
            isDark
              ? "bg-gradient-to-r from-white/10 via-white/35 to-white/10"
              : "bg-gradient-to-r from-black/5 via-black/18 to-black/5"
          }`}
        />

        {/* Header */}
        <div className="px-8 pt-7 pb-0 flex-shrink-0">
          <div className="flex items-center gap-3 mb-1">
            <span className="text-2xl">🛒</span>
            <h2
              className={`font-syne font-bold text-xl tracking-tight ${
                isDark ? "text-white" : "text-[#111]"
              }`}
            >
              What do you flip?
            </h2>
          </div>
          <p
            className={`font-inter text-sm leading-relaxed mt-1 ${
              isDark ? "text-[#555]" : "text-[#999]"
            }`}
          >
            Pick everything you buy &amp; sell — we&apos;ll personalise your experience.
          </p>

          {/* Selection count pill */}
          <div className="mt-4 mb-5">
            {selected.size === 0 ? (
              <span
                className={`font-inter text-xs ${isDark ? "text-[#444]" : "text-[#bbb]"}`}
              >
                Select at least one category to continue
              </span>
            ) : (
              <span
                className={`inline-flex items-center gap-1.5 font-inter text-xs font-medium px-3 py-1 rounded-full ${
                  isDark
                    ? "bg-white/8 text-white/70"
                    : "bg-black/6 text-[#555]"
                }`}
              >
                <i className="ri-check-line text-[10px]" />
                {selected.size} selected
              </span>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className={`mx-8 h-px flex-shrink-0 ${isDark ? "bg-white/6" : "bg-black/6"}`} />

        {/* Grid — scrollable */}
        <div className="px-8 py-5 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-3">
            {CATEGORIES.map((cat) => {
              const isSelected = selected.has(cat.id);
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => toggle(cat.id)}
                  className={`relative flex items-center gap-4 px-4 py-4 rounded-2xl border text-left cursor-pointer transition-all duration-150 group ${
                    isSelected
                      ? isDark
                        ? "bg-white/10 border-white/30"
                        : "bg-[#111]/6 border-[#111]/30"
                      : isDark
                      ? "bg-white/[0.02] border-white/6 hover:bg-white/5 hover:border-white/15"
                      : "bg-black/[0.015] border-black/6 hover:bg-black/4 hover:border-black/15"
                  }`}
                >
                  {/* Emoji */}
                  <span
                    className={`text-2xl flex-shrink-0 transition-transform duration-150 ${
                      isSelected ? "scale-110" : "group-hover:scale-105"
                    }`}
                  >
                    {cat.emoji}
                  </span>

                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    <p
                      className={`font-syne font-semibold text-sm leading-tight truncate ${
                        isSelected
                          ? isDark ? "text-white" : "text-[#111]"
                          : isDark ? "text-white/80" : "text-[#333]"
                      }`}
                    >
                      {cat.label}
                    </p>
                    <p
                      className={`font-inter text-[11px] mt-0.5 truncate ${
                        isDark ? "text-[#444]" : "text-[#aaa]"
                      }`}
                    >
                      {cat.desc}
                    </p>
                  </div>

                  {/* Check mark */}
                  <div
                    className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center border transition-all duration-150 ${
                      isSelected
                        ? isDark
                          ? "bg-white border-white"
                          : "bg-[#111] border-[#111]"
                        : isDark
                        ? "border-white/15 bg-transparent"
                        : "border-black/15 bg-transparent"
                    }`}
                  >
                    {isSelected && (
                      <i
                        className={`ri-check-line text-[10px] font-bold ${
                          isDark ? "text-black" : "text-white"
                        }`}
                      />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div
          className={`px-8 py-6 flex-shrink-0 border-t ${
            isDark ? "border-white/6 bg-[#0a0a0a]" : "border-black/6 bg-white"
          }`}
        >
          <button
            type="button"
            onClick={handleContinue}
            disabled={selected.size === 0}
            className={`w-full font-inter font-semibold text-sm py-3.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${
              isDark
                ? "bg-white text-black hover:bg-[#e0e0e0]"
                : "bg-[#111] text-white hover:bg-[#333]"
            }`}
          >
            <span className="flex items-center justify-center gap-2">
              Let&apos;s go
              <i className="ri-arrow-right-line text-sm" />
            </span>
          </button>
          <button
            type="button"
            onClick={onSkip}
            className={`mt-3 w-full font-inter text-xs text-center cursor-pointer transition-colors ${
              isDark ? "text-[#3a3a3a] hover:text-[#666]" : "text-[#ccc] hover:text-[#888]"
            }`}
          >
            Skip for now
          </button>
        </div>
      </div>

      <style>{`
        @keyframes cpFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes cpSlideUp {
          from { opacity: 0; transform: translateY(28px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
